import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { channelWa, flows } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Badge, Code, ActionIcon } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { useT } from '../../../../i18n/I18nProvider';
import { StatusPill } from '../../../../components/panel';
import { ICONS } from './nodes';
import { publicApiUrl } from '../../../../lib/public-api';
import { EditWebSiteModal } from './EditWebSiteModal';

/**
 * Where this bot runs, and how the pieces fit: the panel behind "Connect".
 *
 * It says plainly what is true. WhatsApp is live: the numbers on the account,
 * the words that start the flow, whether it is switched on. Messenger and
 * Instagram went live on 9 September 2026 and are connected on Channels, which
 * this now points at rather than calling them unavailable. It also gives the
 * API call that starts the flow from another system, and the recipe for
 * capturing a lead and greeting people by name.
 */
export function ChannelsPanel({
  opened, onClose, flowId, flowName, isActive, triggerType, keywords, onTest
}: {
  opened: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  isActive: boolean;
  triggerType: string;
  keywords: string[];
  onTest: () => void;
}) {
  const t = useT();
  const numbers = useQuery({
    queryKey: ['phone-numbers'],
    queryFn: () => channelWa.listPhoneNumbers(),
    enabled: opened,
    staleTime: 60_000
  });
  const list = (numbers.data?.data ?? []) as Array<{ id: string; msisdn?: string | null; display_name?: string | null; is_active?: boolean }>;
  const firstNumber = list[0]?.msisdn ?? '+27…';
  const firstWord = keywords[0] ?? t('flows.connect.anyMessage');
  const apiBase = publicApiUrl();
  // A shell command, shown verbatim: not copy to translate.
  const numberId = list[0]?.id ?? '<number id>'; // i18n-ignore
  const curl = [
    `curl -X POST ${apiBase}/v1/flows/${flowId}/trigger \\`,
    "  -H 'Authorization: Bearer <api key>' \\", // i18n-ignore
    "  -H 'Content-Type: application/json' \\", // i18n-ignore
    `  -d '{"to": "+27821234567", "number_id": "${numberId}", "variables": {"order_id": "A1234"}}'` // i18n-ignore
  ].join('\n');

  return (
    <Drawer opened={opened} onClose={onClose} title={t('flows.connect.title', { name: flowName })} position="right" size="md">
      <Stack gap="lg" style={{ padding: 4 }}>
        {/* WhatsApp: live. */}
        <section className="rr-connect__section">
          <Group gap="sm" align="center">
            <span className="rr-connect__icon" style={{ color: '#16a34a' }}>{ICONS.whatsapp}</span>
            <Text role="bodySmall" fw={700}>{t('flows.connect.whatsapp')}</Text>
            <StatusPill tone={isActive ? 'active' : 'paused'}>{isActive ? t('flows.connect.live') : t('flows.connect.notActive')}</StatusPill>
          </Group>
          <Text role="bodySmall">
            {triggerType === 'always' || (triggerType === 'message' && keywords.length === 0)
              ? t('flows.connect.whatsappAny', { number: firstNumber })
              : triggerType === 'webhook'
                ? t('flows.connect.whatsappWebhook')
                : t('flows.connect.whatsappKeyword', { number: firstNumber, word: firstWord })}
          </Text>
          {!isActive && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.connect.activateHint')}</Text>}
          <Stack gap={4}>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.connect.numbers')}</Text>
            {numbers.isPending && <Text role="caption" c="var(--rr-color-text-muted)">…</Text>}
            {!numbers.isPending && list.length === 0 && (
              <Text role="bodySmall">
                {t('flows.connect.noNumbers')} <Link to="/numbers">{t('nav.numbers')}</Link>
              </Text>
            )}
            {list.map((n) => (
              <Group key={n.id} gap="xs" align="center">
                <Badge size="sm" variant="light">{n.msisdn ?? n.id}</Badge>
                <Text role="caption" c="var(--rr-color-text-muted)">{n.display_name ?? ''}</Text>
              </Group>
            ))}
          </Stack>
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={onTest}>{t('flows.builder.test')}</Button>
            <Link to="/numbers" className="rr-connect__link">{t('flows.connect.manageNumbers')}</Link>
          </Group>
        </section>

        {/* From another system: the API trigger. */}
        <section className="rr-connect__section">
          <Group gap="sm" align="center">
            <span className="rr-connect__icon" style={{ color: '#2563eb' }}>{ICONS.webhook}</span>
            <Text role="bodySmall" fw={700}>{t('flows.connect.api')}</Text>
          </Group>
          <Text role="bodySmall">{t('flows.connect.apiHint')}</Text>
          <Code block>{curl}</Code>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.connect.apiKeys')} <Link to="/settings">{t('nav.settings')}</Link></Text>
        </section>

        {/* Website: live. A site key and a snippet. */}
        <WebsiteSection apiBase={apiBase} isActive={isActive} />

        {/* Live since 9 September 2026, and connected elsewhere. */}
        <section className="rr-connect__section">
          <Text role="bodySmall" fw={700}>{t('flows.connect.otherChannels')}</Text>
          <ul className="rr-connect__list">
            <li><strong>{t('flows.connect.messenger')}</strong> {t('flows.connect.metaChannelLive')}</li>
            <li><strong>{t('flows.connect.instagram')}</strong> {t('flows.connect.metaChannelLive')}</li>
          </ul>
          <Text role="bodySmall">
            {t('flows.connect.metaChannelWhere')} <Link to="/channels">{t('nav.channels')}</Link>
          </Text>
        </section>

        {/* Leads and personalisation. */}
        <section className="rr-connect__section">
          <Group gap="sm" align="center">
            <span className="rr-connect__icon" style={{ color: '#f97316' }}>{ICONS.user}</span>
            <Text role="bodySmall" fw={700}>{t('flows.connect.leads')}</Text>
          </Group>
          <ol className="rr-connect__steps">
            <li>{t('flows.connect.leads1')}</li>
            <li>{t('flows.connect.leads2')}</li>
            <li>{t('flows.connect.leads3')}</li>
            <li>{t('flows.connect.leads4')}</li>
          </ol>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('flows.connect.knowledgeHint')} <Link to="/knowledge">{t('nav.knowledge')}</Link>
          </Text>
        </section>
      </Stack>
    </Drawer>
  );
}

/**
 * The website channel: register the site, copy the snippet. The same flows
 * answer there as on WhatsApp, chosen by the same rule, so this flow answers
 * on the site the moment it is active and its trigger fires. A site's name,
 * greeting and allowed websites can be changed in place (22 September 2026);
 * removing one asks first, because it stops the chat on every website that
 * carries its snippet.
 */
function WebsiteSection({ apiBase, isActive }: { apiBase: string; isActive: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<flows.WebSite | null>(null);
  const sites = useQuery({ queryKey: ['web-sites'], queryFn: () => flows.listWebSites(), staleTime: 30_000 });
  const list = sites.data?.data ?? [];
  const create = useMutation({
    mutationFn: () => flows.createWebSite({ name: name.trim() }),
    onSuccess: () => { setName(''); qc.invalidateQueries({ queryKey: ['web-sites'] }); notifySuccess(t('flows.connect.siteCreated'), t('flows.connect.website')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('flows.connect.website'))
  });
  const remove = useMutation({
    mutationFn: (id: string) => flows.deleteWebSite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web-sites'] }),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('flows.connect.website'))
  });
  const snippet = (key: string) => `<script src="${apiBase}/v1/channels/web/widget.js" data-site="${key}"></script>`;
  const confirmRemove = (site: flows.WebSite) => openConfirmModal({
    title: t('flows.connect.removeSiteTitle', { name: site.name }),
    children: <Text role="bodySmall">{t('flows.connect.removeSiteBody')}</Text>,
    labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
    confirmProps: { color: 'red' },
    onConfirm: () => remove.mutate(site.id)
  });
  return (
    <section className="rr-connect__section">
      <Group gap="sm" align="center">
        <span className="rr-connect__icon" style={{ color: '#7c3aed' }}>{ICONS.globe ?? ICONS.webhook}</span>
        <Text role="bodySmall" fw={700}>{t('flows.connect.website')}</Text>
        <StatusPill tone={isActive ? 'active' : 'paused'}>{isActive ? t('flows.connect.live') : t('flows.connect.notActive')}</StatusPill>
      </Group>
      <Text role="bodySmall">{t('flows.connect.websiteIntro')}</Text>
      {list.map((site) => (
        <Stack key={site.id} gap={4}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text role="bodySmall" fw={650} truncate>{site.name}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)" truncate>
                {site.allowed_origins?.length ? t('flows.connect.onSites', { sites: site.allowed_origins.join(', ') }) : t('flows.connect.onAnySite')}
              </Text>
            </Stack>
            <Group gap={4} wrap="nowrap">
              <Button size="xs" variant="subtle" onClick={() => setEditing(site)}>{t('action.edit')}</Button>
              <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => confirmRemove(site)}>×</ActionIcon>
            </Group>
          </Group>
          <Code block>{snippet(site.site_key)}</Code>
        </Stack>
      ))}
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <TextInput
          aria-label={t('flows.connect.siteName')}
          placeholder={t('flows.connect.siteNamePlaceholder')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button size="xs" variant="default" disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate()}>
          {list.length ? t('flows.connect.addSite') : t('flows.connect.createSite')}
        </Button>
      </Group>
      <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.connect.websiteSnippetHint')}</Text>
      {editing && <EditWebSiteModal site={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}
